import React, { useMemo, useState } from "react";
import { AGE_VALUES, LEAD_ENDPOINT, WHATSAPP_URL } from "./homeContent";
import "./Home.css";

export function track(event, data) {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;
  try {
    window.fbq("track", event, data || {});
  } catch (e) {
    // Tracking must never break the page.
  }
}

// text/plain avoids the CORS preflight that Apps Script cannot answer;
// the body is still JSON and is parsed as such on the server.
function sendLead(payload) {
  const body = JSON.stringify(payload);
  const type = "text/plain;charset=UTF-8";
  try {
    if (navigator.sendBeacon && navigator.sendBeacon(LEAD_ENDPOINT, new Blob([body], { type }))) {
      return;
    }
  } catch (e) {
    // Fall through to fetch.
  }
  fetch(LEAD_ENDPOINT, {
    method: "POST",
    mode: "no-cors",
    headers: { "Content-Type": type },
    body,
    keepalive: true,
  }).catch(() => {});
}

function isValidPhone(value) {
  const digits = value.replace(/\D/g, "");
  return /^[+\d][\d\s().-]*$/.test(value) && digits.length >= 8 && digits.length <= 15;
}

export function Check({ className = "" }) {
  return <i className={`fa-solid fa-circle-check ${className}`} aria-hidden="true" />;
}

export function Reassure({ items, align }) {
  return (
    <div className={`hf-reassure${align === "start" ? " hf-reassure--start" : ""}`}>
      {items.map((item) => (
        <span key={item}>
          <Check />
          {item}
        </span>
      ))}
    </div>
  );
}

export default function LeadForm({ c, language, ctaSource }) {
  const [step, setStep] = useState(1);
  const [age, setAge] = useState("");
  const [values, setValues] = useState({ parent: "", child: "", tel: "" });
  const [errors, setErrors] = useState({});
  const [done, setDone] = useState(false);

  const tracking = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get("utm_source") || "",
      utm_campaign: params.get("utm_campaign") || "",
      utm_content: params.get("utm_content") || "",
      fbclid: params.get("fbclid") || "",
    };
  }, []);

  const update = (event) => {
    const { name, value } = event.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const pickAge = (index) => {
    setAge(AGE_VALUES[index]);
    setStep(2);
  };

  const submit = (event) => {
    event.preventDefault();
    const parent = values.parent.trim();
    const child = values.child.trim();
    const tel = values.tel.trim();
    const nextErrors = {
      parent: parent.length < 2,
      child: child.length < 2,
      tel: !isValidPhone(tel),
    };
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    sendLead({
      date: new Date().toISOString(),
      source: "site_casaclassroom",
      prenom_parent: parent,
      prenom_enfant: child,
      whatsapp: tel,
      age_enfant: age,
      cta_source: ctaSource,
      langue: language,
      page: window.location.pathname,
      ...tracking,
    });
    track("Lead", { content_name: "seance_gratuite", age });
    setDone(true);
  };

  if (done) {
    return (
      <div className="hf-formbox hf-done">
        <div className="hf-done__ic">
          <i className="fa-solid fa-check" aria-hidden="true" />
        </div>
        <h3>
          {c.form.doneH} {values.parent.trim()} !
        </h3>
        <p>
          {c.form.doneP} {values.child.trim() || c.form.doneChild}.
        </p>
        <a
          className="hf-btn hf-btn--primary hf-btn--block"
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track("Contact", { method: "whatsapp", emplacement: "confirmation" })}
        >
          {c.form.doneWa}
        </a>
        <p className="hf-privacy">{c.form.doneClose}</p>
      </div>
    );
  }

  return (
    <div className="hf-formbox">
      <div className="hf-prog">
        <div className="hf-prog__bar">
          <div className="hf-prog__fill" style={{ width: step === 1 ? "50%" : "100%" }} />
        </div>
        <span className="hf-prog__txt">{c.form.step.replace("{n}", step)}</span>
      </div>

      {step === 1 ? (
        <div className="hf-pane">
          <h3>{c.form.s1h}</h3>
          <p className="hf-sub">{c.form.s1sub}</p>
          <div className="hf-ages" role="group" aria-label={c.form.s1h}>
            {c.form.ages.map((label, index) => (
              <button
                type="button"
                key={label}
                className={`hf-age${age === AGE_VALUES[index] ? " is-selected" : ""}`}
                onClick={() => pickAge(index)}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="hf-age-note">{c.form.s1note}</p>
        </div>
      ) : (
        <form className="hf-pane" onSubmit={submit} noValidate>
          <h3>{c.form.s2h}</h3>
          <p className="hf-sub">{c.form.s2sub}</p>
          <div className="hf-row2">
            <div className={`hf-field${errors.parent ? " is-bad" : ""}`}>
              <label htmlFor="hf-parent">{c.form.parent}</label>
              <input id="hf-parent" name="parent" type="text" autoComplete="given-name" placeholder={c.form.phParent} value={values.parent} onChange={update} />
              {errors.parent && <span className="hf-err">{c.form.req}</span>}
            </div>
            <div className={`hf-field${errors.child ? " is-bad" : ""}`}>
              <label htmlFor="hf-child">{c.form.child}</label>
              <input id="hf-child" name="child" type="text" placeholder={c.form.phChild} value={values.child} onChange={update} />
              {errors.child && <span className="hf-err">{c.form.req}</span>}
            </div>
          </div>
          <div className={`hf-field${errors.tel ? " is-bad" : ""}`}>
            <label htmlFor="hf-tel">{c.form.tel}</label>
            <input id="hf-tel" name="tel" type="tel" inputMode="tel" autoComplete="tel" placeholder={c.form.phTel} value={values.tel} onChange={update} />
            {errors.tel && <span className="hf-err">{c.form.errTel}</span>}
            <span className="hf-hint">{c.form.hint}</span>
          </div>
          <button type="submit" className="hf-btn hf-btn--primary hf-btn--block">
            {c.cta.book}
          </button>
          <Reassure items={[c.r.free, c.r.nocommit, c.r.s20]} />
          <button type="button" className="hf-back" onClick={() => setStep(1)}>
            <i className="fa-solid fa-arrow-left hf-flip" aria-hidden="true" />
            {c.form.back}
          </button>
          <p className="hf-privacy">{c.form.privacy}</p>
        </form>
      )}
    </div>
  );
}
